/**
 * DemandesRdvList — composant partagé pour Admin et Partenaire.
 *
 * Props :
 *  - apiBase : ex `${API}/rdv-demandes` ou `${API}/partner/rdv-demandes`
 *  - getAuthHeaders : () => { Authorization: 'Bearer …' }
 *  - scopeLabel : libellé affiché en titre (admin global vs garage scope)
 *  - showGarageColumn : boolean (true côté admin, false côté partenaire)
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Inbox, Phone, Mail, CalendarDays, Car, Search, RefreshCw, AlertCircle, Loader2,
  CheckCircle2, MessageSquare, XCircle, Clock,
} from 'lucide-react';

const STATUS_LIST = [
  { key: 'nouveau',  label: 'Nouveau',  color: 'bg-blue-100 text-blue-800 border-blue-200' },
  { key: 'contacte', label: 'Contacté', color: 'bg-amber-100 text-amber-800 border-amber-200' },
  { key: 'planifie', label: 'Planifié', color: 'bg-green-100 text-green-800 border-green-200' },
  { key: 'refuse',   label: 'Refusé',   color: 'bg-gray-100 text-gray-700 border-gray-200' },
];
const INTERVENTION_LABELS = {
  service: 'Service / entretien',
  pneus: 'Pneus',
  carrosserie: 'Carrosserie',
  diagnostic: 'Diagnostic',
  controle: 'Contrôle technique',
  autre: 'Autre',
};

const formatFR = (iso) => {
  if (!iso) return '';
  const d = iso.includes('T') ? new Date(iso) : null;
  if (d) {
    return d.toLocaleString('fr-CH', { dateStyle: 'short', timeStyle: 'short' });
  }
  const [y, m, day] = iso.split('-');
  return `${day}.${m}.${y}`;
};

const DemandesRdvList = ({ apiBase, getAuthHeaders, scopeLabel, showGarageColumn = false }) => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ nouveau: 0, contacte: 0, planifie: 0, refuse: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [search, setSearch] = useState('');
  const [activeDemande, setActiveDemande] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [internalNote, setInternalNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(apiBase, {
        headers: getAuthHeaders(),
        params: { statut: filterStatut || undefined, q: search || undefined },
        timeout: 12000,
      });
      setItems(data.items || []);
      setStats(data.stats || {});
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Erreur de chargement';
      setError(typeof msg === 'string' ? msg : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [apiBase, getAuthHeaders, filterStatut, search]);

  useEffect(() => { load(); }, [load]);

  const openDetail = (d) => {
    setActiveDemande(d);
    setInternalNote(d.internal_note || '');
  };

  const updateStatut = async (newStatut) => {
    if (!activeDemande) return;
    setActionLoading(true);
    try {
      const { data } = await axios.patch(
        `${apiBase}/${activeDemande.id}`,
        { statut: newStatut, internal_note: internalNote || null },
        { headers: getAuthHeaders(), timeout: 12000 },
      );
      setActiveDemande(data.item);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setActionLoading(false);
    }
  };

  const saveNote = async () => {
    if (!activeDemande) return;
    setActionLoading(true);
    try {
      const { data } = await axios.patch(
        `${apiBase}/${activeDemande.id}`,
        { internal_note: internalNote || '' },
        { headers: getAuthHeaders(), timeout: 12000 },
      );
      setActiveDemande(data.item);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setActionLoading(false);
    }
  };

  const statusBadge = (statut) => {
    const cfg = STATUS_LIST.find((s) => s.key === statut);
    return (
      <Badge variant="outline" className={`${cfg?.color || ''} text-[11px] font-medium`}>
        {cfg?.label || statut}
      </Badge>
    );
  };

  return (
    <div className="space-y-4" data-testid="demandes-rdv-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Inbox className="w-6 h-6 text-[#52a675]" /> Demandes RDV</h1>
          <p className="text-sm text-gray-500">{scopeLabel}</p>
        </div>
        <Button onClick={load} variant="outline" size="sm" disabled={loading} data-testid="btn-rdv-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Actualiser
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <button
          type="button"
          onClick={() => setFilterStatut('')}
          className={`rounded-lg border p-3 text-left transition ${filterStatut === '' ? 'border-[#52a675] bg-[#52a675]/10' : 'border-gray-200 hover:border-[#52a675]/50'}`}
          data-testid="rdv-filter-all"
        >
          <div className="text-[11px] text-gray-500 uppercase tracking-wider">Total</div>
          <div className="text-xl font-bold">{stats.total || 0}</div>
        </button>
        {STATUS_LIST.map((s) => (
          <button
            type="button"
            key={s.key}
            onClick={() => setFilterStatut(filterStatut === s.key ? '' : s.key)}
            className={`rounded-lg border p-3 text-left transition ${filterStatut === s.key ? 'border-[#52a675] bg-[#52a675]/10' : 'border-gray-200 hover:border-[#52a675]/50'}`}
            data-testid={`rdv-filter-${s.key}`}
          >
            <div className="text-[11px] text-gray-500 uppercase tracking-wider">{s.label}</div>
            <div className="text-xl font-bold">{stats[s.key] || 0}</div>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input
          placeholder="Rechercher (nom, email, téléphone, véhicule, plaque)…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="rdv-search"
        />
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="py-12 text-center text-gray-500"><Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Chargement…</div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center text-gray-500">
            <Inbox className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            Aucune demande pour ce filtre.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((d) => (
            <button
              type="button"
              key={d.id}
              onClick={() => openDetail(d)}
              className="w-full text-left rounded-xl border border-gray-200 bg-white hover:border-[#52a675]/60 hover:shadow-md transition p-4 flex flex-wrap gap-3 items-center"
              data-testid={`rdv-row-${d.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {statusBadge(d.statut)}
                  <span className="text-sm font-semibold text-gray-900">{d.name}</span>
                  <span className="text-xs text-gray-500">
                    <CalendarDays className="w-3 h-3 inline mr-0.5" />
                    {formatFR(d.slot_date)} · {d.slot_period === 'matin' ? 'Matin' : 'Après-midi'}
                  </span>
                </div>
                <div className="text-xs text-gray-600 flex items-center gap-3 flex-wrap">
                  <span><Phone className="w-3 h-3 inline mr-0.5" /> {d.phone}</span>
                  <span><Mail className="w-3 h-3 inline mr-0.5" /> {d.email}</span>
                  <span><Car className="w-3 h-3 inline mr-0.5" /> {d.vehicle}{d.plate ? ` · ${d.plate}` : ''}</span>
                  <span className="text-[#3d8a5c] font-medium">{INTERVENTION_LABELS[d.intervention_type] || d.intervention_type}</span>
                  {showGarageColumn && d.garage_slug && (
                    <Badge variant="outline" className="text-[10px]">{d.garage_slug}</Badge>
                  )}
                </div>
              </div>
              <div className="text-xs text-gray-400 whitespace-nowrap">
                <Clock className="w-3 h-3 inline mr-0.5" />
                {formatFR(d.created_at)}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!activeDemande} onOpenChange={(o) => !o && setActiveDemande(null)}>
        <DialogContent className="sm:max-w-lg">
          {activeDemande && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  Demande de {activeDemande.name}
                  {statusBadge(activeDemande.statut)}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[11px] uppercase text-gray-500">Date souhaitée</div>
                    <div className="font-semibold">{formatFR(activeDemande.slot_date)} · {activeDemande.slot_period === 'matin' ? 'Matin' : 'Après-midi'}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase text-gray-500">Type</div>
                    <div className="font-semibold">{INTERVENTION_LABELS[activeDemande.intervention_type] || activeDemande.intervention_type}</div>
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase text-gray-500">Véhicule</div>
                  <div className="font-medium">{activeDemande.vehicle}{activeDemande.plate ? ` · ${activeDemande.plate}` : ''}</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[11px] uppercase text-gray-500">Téléphone</div>
                    <a href={`tel:${activeDemande.phone}`} className="font-medium text-[#52a675]">{activeDemande.phone}</a>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase text-gray-500">Email</div>
                    <a href={`mailto:${activeDemande.email}`} className="font-medium text-[#52a675] break-all">{activeDemande.email}</a>
                  </div>
                </div>
                {activeDemande.message && (
                  <div>
                    <div className="text-[11px] uppercase text-gray-500">Message du client</div>
                    <p className="bg-gray-50 rounded-md p-2 text-gray-800 whitespace-pre-wrap">{activeDemande.message}</p>
                  </div>
                )}
                <div>
                  <div className="text-[11px] uppercase text-gray-500 mb-1">Note interne</div>
                  <Textarea
                    rows={2}
                    value={internalNote}
                    onChange={(e) => setInternalNote(e.target.value)}
                    placeholder="Note interne (visible uniquement par l'équipe)"
                    maxLength={2000}
                    data-testid="rdv-detail-note"
                  />
                  <Button variant="outline" size="sm" onClick={saveNote} disabled={actionLoading} className="mt-2" data-testid="rdv-detail-save-note">
                    {actionLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <MessageSquare className="w-3 h-3 mr-1" />}
                    Enregistrer la note
                  </Button>
                </div>
                {Array.isArray(activeDemande.history) && activeDemande.history.length > 0 && (
                  <details className="text-xs text-gray-500">
                    <summary className="cursor-pointer hover:text-gray-700">Historique ({activeDemande.history.length})</summary>
                    <ul className="mt-2 space-y-1 pl-3">
                      {activeDemande.history.map((h, i) => (
                        <li key={i}>
                          <span className="text-gray-400">{formatFR(h.at)}</span> — {h.action}
                          {h.statut ? ` → ${h.statut}` : ''} <span className="text-gray-400">({h.by})</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>

              <DialogFooter className="flex flex-wrap gap-2 sm:justify-between">
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    onClick={() => updateStatut('contacte')}
                    disabled={actionLoading || activeDemande.statut === 'contacte'}
                    data-testid="rdv-action-contacte"
                  >
                    <Phone className="w-3 h-3 mr-1" /> Contacté
                  </Button>
                  <Button
                    size="sm"
                    className="bg-[#52a675] hover:bg-[#3d8a5c]"
                    onClick={() => updateStatut('planifie')}
                    disabled={actionLoading || activeDemande.statut === 'planifie'}
                    data-testid="rdv-action-planifie"
                  >
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Planifié
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    onClick={() => updateStatut('refuse')}
                    disabled={actionLoading || activeDemande.statut === 'refuse'}
                    data-testid="rdv-action-refuse"
                  >
                    <XCircle className="w-3 h-3 mr-1" /> Refusé
                  </Button>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setActiveDemande(null)}>
                  Fermer
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DemandesRdvList;
